import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ComponentView } from 'src/modules/app-template/models/component-view.model';
import { ButtonDefinition } from 'src/modules/sm-base/models/button-definition.model';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { TableData } from 'src/modules/sm-base/models/table-data.model';
import { TableRow } from 'src/modules/sm-base/models/table-row.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldListItem } from 'src/modules/sm-base/netgen/FrontendFieldListItem';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { TableCellType } from 'src/modules/sm-base/netgen/TableCellType';
import { TableColumn } from 'src/modules/sm-base/netgen/TableColumn';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { ObjectOfSimpleDataTypes } from 'src/modules/utils/shared/object-of-simple-data-types.model';
import { Objects } from 'src/modules/utils/shared/objects';
import { Strings } from 'src/modules/utils/shared/strings';
import { Utils } from 'src/modules/utils/shared/utils';
import { EditProjectContactPersonDto } from '../../netgen/EditProjectContactPersonDto';
import { EditProjectResponsibilitesDto } from '../../netgen/EditProjectResponsibilitesDto';
import { EditProjectRoleDto } from '../../netgen/EditProjectRoleDto';
import { ProjectDto } from '../../netgen/ProjectDto';
import { ProjectPlanningService } from '../../services/project-planning.service';

@Component({
  selector: 'app-edit-project-responsibilities',
  standalone: false,
  templateUrl: './edit-project-responsibilities.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './edit-project-responsibilities.component.scss',
})
export class EditProjectResponsibilitiesComponent extends ComponentView {

    service = inject(ProjectPlanningService);
    
    neededParams: ObjectOfSimpleDataTypes = { id: "number" };
    id = 0;

    form: FrontendForm;

    tableRoles: TableData;
    tableContactPersons: TableData;
    project: ProjectDto;

    async initParams(): Promise<boolean> {
        this.project = await this.service.getProjectDto(this.id, true);
        let users = await this.service.getActiveUsers();
        let roles = await this.service.getRoles();
        let roleKeysAll = Arrays.explode(this.project.events, e => e.roles.filter(r => r.roleId != null).map(r => r.roleId + "#" + e.structurePath));
        let roleKeys = Arrays.getUnique(roleKeysAll);
        let spKeysAll = this.project.events.map(e => e.structurePath);
        let spKeys = Arrays.getUnique(spKeysAll);

        this.tableRoles = new TableData([
            new TableColumn("current", this.app.t("entity.Project.other.currentAssignments"), TableCellType.string, 20, {
                styleClass: "xbg-gray-200"
            }),           
            new TableColumn("count", this.app.t("entity.Project.other.tasksCount"), TableCellType.string, 5, {
                styleClass: "xbg-gray-200"
            }),
            new TableColumn("unassignedCount", this.app.t("entity.Project.other.unassignedTasksCount"), TableCellType.string, 5, {
                styleClass: "xbg-gray-200"
            }),
            new TableColumn("structurePath", this.app.t("entity.CalendarOutlineEvent.fields.structurePath"), TableCellType.string, 20, {
                styleClass: "xbg-gray-200"
            }),
            new TableColumn("role", this.app.t("entity.CalendarEventRole.fields.roleId"), TableCellType.string, 20, {
                styleClass: "xbg-gray-200"
            }),
            new TableColumn("responsibleId", this.app.t("entity.Project.other.newAssignment"), TableCellType.int, 50, { //TODO 50 ist notwendig, weil Größenberechnung für int anders funktioniert
                editable: true,
                editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                    listItems: FrontendFieldListItem.make(users, item => [item.id, item.getFullName()], true, [[null, this.app.t("entity.Project.other.noEmployee")]]),
                    dropdownCanFilter: true
                }),
                valueFormatter: e => users.find(item => item.id == e.value)?.getFullName() ?? this.app.t("entity.Project.other.noEmployee")
            })
        ], roleKeys.map(key => {
            let roleId = Utils.toNumber(Strings.getSubstringTo(key, "#"));
            let structurePath = Strings.getSubstringFrom(key, "#");
            let assignments = Arrays.explode(this.project.events, e => e.structurePath == structurePath ? e.roles.filter(r => r.roleId == roleId) : []);

            return new TableRow(key, key, {
                current: Arrays.itemsToString(Arrays.sort(Arrays.getUnique(assignments.filter(role => role.responsibleId != null).map(role => users.find(u => u.id == role.responsibleId).getFullName())))),
                count: assignments.length,
                unassignedCount: assignments.filter(role => role.responsibleId == null).length,
                structurePath: this.service.formatStructurePathString(structurePath),
                role: roles.find(r => r.id == roleId).title,
                responsibleId: null
            });
        }), ["role", "structurePath"]);

        this.tableContactPersons = new TableData([
            new TableColumn("current", this.app.t("entity.Project.other.currentAssignments"), TableCellType.string, 20, {
                styleClass: "xbg-gray-200"
            }),           
            new TableColumn("count", this.app.t("entity.Project.other.tasksCount"), TableCellType.string, 5, {
                styleClass: "xbg-gray-200"
            }),           
            new TableColumn("structurePath", this.app.t("entity.CalendarOutlineEvent.fields.structurePath"), TableCellType.string, 20, {
                styleClass: "xbg-gray-200"
            }),
            new TableColumn("responsibleId", this.app.t("entity.Project.other.newAssignment"), TableCellType.int, 50, { //TODO 50 ist notwendig, weil Größenberechnung für int anders funktioniert
                editable: true,
                editor: new FrontendField("", "", FrontendFieldType.dropdown, {
                    listItems: FrontendFieldListItem.make(users, item => [item.id, item.getFullName()], true, [[null, this.app.t("entity.Project.other.noEmployee")]]),
                    dropdownCanFilter: true
                }),
                valueFormatter: e => users.find(item => item.id == e.value)?.getFullName() ?? this.app.t("entity.Project.other.noEmployee")
            })
        ], spKeys.map(key => new TableRow(key, key, {
            current: Arrays.itemsToString(Arrays.sort(Arrays.getUnique(this.project.events.filter(e => e.structurePath == key).map(e => users.find(u => u.id == e.contactPersonId).getFullName())))),
            count: spKeysAll.filter(k => k == key).length,
            structurePath: this.service.formatStructurePathString(key),
            responsibleId: null
        })), ["structurePath"]);
        
        return true;
    }

    getDialogButtons(): ButtonDefinition[] {
        return [
            new ButtonDefinition("save", this.app.t("general.save"), true, "fas fa-save", "", "success"),
            new ButtonDefinition("cancel", this.app.t("general.cancel"), true, "fas fa-times", "", "secondary")
        ];
    }

    async buttonClickedFromExtern(id: string): Promise<void> {
        if (id == "save") {
            await this.save();
        }
        else if (id == "cancel") {
            await this.close();
        }
    }

    async save(): Promise<void> {
        let dto = new EditProjectResponsibilitesDto();
        dto.projectId = this.id;
        dto.roles = this.tableRoles.rows.filter(row => row.values.responsibleId != null).map(row => Objects.fromPlain(EditProjectRoleDto, {
            roleId: Utils.toNumber(Strings.getSubstringTo(row.id, "#")),
            structurePath: Strings.getSubstringFrom(row.id, "#"),
            userId: row.values.responsibleId as number,
            overwrite: true
        }));
        dto.contactPersons = this.tableContactPersons.rows.filter(row => row.values.responsibleId != null).map(row => Objects.fromPlain(EditProjectContactPersonDto, {
            structurePath: row.id,
            userId: row.values.responsibleId as number
        }));
        await RestEndpoint.main().post().body(dto).run("api/projectplanning/project/responsibilities").getText();
        await this.close();
    }
}
